
import React, { useState, useEffect, useRef } from 'react';
import { Search, ChefHat, Info, Star, SlidersHorizontal, Sparkles, ArrowUp, Ticket, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useExperience } from '../../../stores/Experience/Experience';

const THEME_COLOR = '#c59253';

const CURRENCIES = [

    { code: 'QAR', symbol: 'QR', name: 'Qatari Riyal' },
    { code: 'DZD', symbol: 'DZD', name: 'Algerian Dinar' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'SAR', symbol: '⃁', name: 'Saudi Riyal' },
    { code: 'AED', symbol: 'AED', name: 'UAE Dirham' },
];



const MobileView = ({ state }) => {
    const [lang, setLang] = useState('en'); // 'en' or 'ar'
    const [activeTab, setActiveTab] = useState(null);
    const [showFab, setShowFab] = useState(false);
    const scrollContainerRef = useRef(null);
    const terminology = useExperience.getState().getTerminology();
    const { siteInfo, sections = [], items = [], businessType } = state;
    const businessName = siteInfo?.businessName || siteInfo?.name || `${terminology.business} Name`;
    const logoPreview = siteInfo?.logoPreview || siteInfo?.imagePreview || siteInfo?.image_url;

    const address = siteInfo?.address || '';

    const currency = state.currency || siteInfo?.currency || 'QAR';
    const currencySymbol = CURRENCIES.find(c => c.code === currency)?.symbol || '$';
    // Group items by section
    const itemsBySection = sections.map(section => ({
        ...section,
        items: section.items || []
    })).filter(section => section.items.length > 0);

    useEffect(() => {
        if (itemsBySection.length > 0 && !activeTab) {
            setActiveTab(itemsBySection[0].id);
        }
    }, [itemsBySection]);

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            setShowFab(scrollContainerRef.current.scrollTop > 200);
        }
    };

    const scrollToTop = () => {
        scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="h-100 bg-white d-flex flex-column position-relative" style={{ overflow: 'hidden', fontFamily: "'Inter', sans-serif" }} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
            {/* 1. Header Area (Sticky) */}
            <div className="bg-white px-3 py-2 border-bottom shadow-sm">
                <div className="d-flex align-items-center gap-2">
                    {logoPreview ? (
                        <img
                            src={logoPreview}
                            alt="Logo"
                            className="rounded-3 shadow-sm border"
                            style={{ width: '45px', height: '45px', objectFit: 'cover' }}
                        />
                    ) : (
                        <div className="rounded-3 bg-light d-flex align-items-center justify-content-center border" style={{ width: '45px', height: '45px' }}>
                            <ChefHat size={24} className="text-muted" />
                        </div>
                    )}
                    <div className="flex-grow-1 overflow-hidden">
                        <h6 className="mb-0 fw-bold text-dark">{lang === 'ar' && (siteInfo?.businessName_ar || siteInfo?.name_ar) ? (siteInfo.businessName_ar || siteInfo.name_ar) : businessName}</h6>
                        {(lang === 'ar' ? (siteInfo?.address_ar || address) : address) && (
                            <p className={`mb-0 text-muted x-small text-truncate ${lang === 'ar' ? 'text-end' : ''}`} style={{ fontSize: '11px' }}>
                                {lang === 'ar' ? (siteInfo?.address_ar || address) : address}
                            </p>
                        )}
                    </div>
                    {/* Language Toggle */}
                    <div className="bg-light rounded-pill p-1 d-flex gap-1" style={{ border: '1px solid #eee' }}>
                        <button
                            onClick={() => setLang('en')}
                            className={`btn btn-sm rounded-circle p-0 d-flex align-items-center justify-content-center transition-all ${lang === 'en' ? 'bg-white shadow-sm fw-bold' : 'text-muted'}`}
                            style={{ width: '28px', height: '28px', fontSize: '10px' }}
                        >
                            EN
                        </button>
                        <button
                            onClick={() => setLang('ar')}
                            className={`btn btn-sm rounded-circle p-0 d-flex align-items-center justify-content-center transition-all ${lang === 'ar' ? 'bg-white shadow-sm fw-bold font-arabic' : 'text-muted'}`}
                            style={{ width: '28px', height: '28px', fontSize: '10px' }}
                        >
                            AR
                        </button>
                    </div>
                </div>
            </div>

            {/* Scrollable Area */}
            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex-grow-1 overflow-auto bg-light"
            >


                {/* 3. Search & Filters */}
                <div className="bg-white p-3 d-flex gap-2">
                    <div className="flex-grow-1 border rounded-3 d-flex align-items-center px-2 bg-light bg-opacity-50">
                        <Search size={16} className="text-muted me-2" />
                        <input
                            type="text"
                            className="form-control form-control-sm border-0 bg-transparent shadow-none"
                            placeholder="Search dishes, e.g., pasta"
                        />
                    </div>
                    <button className="btn btn-sm btn-outline-secondary border d-flex align-items-center gap-1 px-2 rounded-3 bg-white">
                        <SlidersHorizontal size={16} />
                        <span className="small fw-medium">Filters</span>
                    </button>
                </div>

                {/* 4. Category Nav (Sticky below Search in logic, but here inside scroll) */}
                <div className="sticky-top bg-white border-bottom py-2 px-3 d-flex gap-2 overflow-auto no-scrollbar" style={{ zIndex: 10 }}>
                    {itemsBySection.map(section => (
                        <button
                            key={section.id}
                            className={`btn btn-sm rounded-pill px-3 py-1 text-nowrap transition-all border ${activeTab === section.id ? 'border-transparent' : 'border-light'}`}
                            style={{
                                backgroundColor: activeTab === section.id ? '#c49553' : '#f1f1f1',
                                color: activeTab === section.id ? 'white' : '#444',
                                fontSize: '12px',
                                fontWeight: '600'
                            }}
                            onClick={() => setActiveTab(section.id)}
                        >
                            {lang === 'ar' && section.name_ar ? section.name_ar : section.name}
                        </button>
                    ))}
                </div>



                {/* 6. Menu Content */}
                <div className="p-3 mt-2 pt-0 pb-5">
                    {itemsBySection.map(section => (
                        <div key={section.id} className="mb-4">
                            <h5 className={`fw-bold mb-1 text-dark ${lang === 'ar' ? 'text-end' : ''}`}>{lang === 'ar' && section.name_ar ? section.name_ar : section.name}</h5>
                            {(section.description || section.description_ar) && (
                                <p className={`text-muted x-small mb-3 ${lang === 'ar' ? 'text-end font-arabic' : ''}`} style={{ fontSize: '10px' }}>
                                    {lang === 'ar' && section.description_ar ? section.description_ar : section.description}
                                </p>
                            )}

                            <div className="d-flex flex-column gap-3 mt-3">
                                {section.items.map(item => (
                                    <motion.div
                                        key={item.id}
                                        whileTap={{ scale: 0.98 }}
                                        className="bg-white rounded-3 border-light border shadow-sm p-0 overflow-hidden"
                                    >
                                        <div className={`d-flex ${lang === 'ar' ? 'flex-row-reverse' : ''}`}>
                                            {item.imagePreview && (
                                                <div className={`p-2 ${lang === 'ar' ? 'ps-0' : 'pe-0'}`} style={{ width: '100px', height: '100px' }}>
                                                    <img src={item.imagePreview} className="w-100 h-100 rounded-3" style={{ objectFit: 'cover' }} alt={item.name} />
                                                </div>
                                            )}
                                            <div className="flex-grow-1 p-3 d-flex flex-column justify-content-between position-relative">
                                                <div className="mb-2">
                                                    <div className={`d-flex align-items-center gap-2 mb-1 ${lang === 'ar' ? 'flex-row-reverse' : ''}`}>
                                                        <h6 className="fw-bold mb-0">{lang === 'ar' && item.name_ar ? item.name_ar : item.name}</h6>
                                                        {item.is_promo && (
                                                            <span className="badge bg-danger rounded-pill px-2 py-1" style={{ fontSize: '8px' }}>PROMO</span>
                                                        )}
                                                    </div>
                                                    <p className={`text-muted mb-1 ${lang === 'ar' ? 'text-end' : ''}`} style={{ fontSize: '11px', lineHeight: '1.3' }}>
                                                        {lang === 'ar' && item.description_ar ? item.description_ar : (item.description || 'No description available')}
                                                    </p>
                                                </div>
                                                <div className={`${lang === 'ar' ? 'text-start' : 'text-end'} mt-auto`}>
                                                    {item.is_promo && item.promo_price ? (
                                                        <div className="d-flex align-items-center justify-content-end gap-2">
                                                            <span className="text-muted text-decoration-line-through small" style={{ fontSize: '10px' }}>{currency}{item.price}</span>
                                                            <span className="fw-bold text-danger">{currency}{item.promo_price}</span>
                                                        </div>
                                                    ) : item.price ? (
                                                        <span className="small fw-bold text-dark">{currency}{item.price}</span>
                                                    ) : (
                                                        <span className="small fw-bold text-dark">Free</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 7. FAB (Float above bottom bar) */}
            <AnimatePresence>
                {showFab && (
                    <motion.button
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.5 }}
                        onClick={scrollToTop}
                        className="position-absolute btn shadow-lg d-flex align-items-center justify-content-center rounded-circle border-0"
                        style={{
                            bottom: '90px',
                            right: '20px',
                            width: '40px',
                            height: '40px',
                            backgroundColor: '#c49553',
                            color: 'white',
                            zIndex: 100
                        }}
                    >
                        <ArrowUp size={20} />
                    </motion.button>
                )}
            </AnimatePresence>

            {/* 8. Bottom Action Bar */}
            {/* <div className="bg-white p-3 border-top shadow-lg d-flex align-items-center justify-content-between" style={{ zIndex: 110 }}>
                <button
                    className="btn rounded-pill px-4 py-2 text-white d-flex align-items-center gap-2 fw-bold shadow-sm"
                    style={{ backgroundColor: '#ff4d6d', border: 'none' }}
                >
                    <Ticket size={18} />
                    <span>Submit</span>
                </button>
                <div className="bg-light px-3 py-2 rounded-pill border d-flex align-items-center gap-1 shadow-inner">
                    <span className="fw-bold text-dark px-2">{currencySymbol} 0.0</span>
                </div>
            </div> */}

            <style>{`
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                .truncate-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                .shadow-inner {
                    box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);
                }
                .text-nowrap {
                    white-space: nowrap !important;
                }
            `}</style>
        </div>
    );
};

// Add CSS for Arabic font support and other live styles
const style = document.createElement('style');
style.innerHTML = `
    .font-arabic {
        font-family: 'Outfit', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
    }
    .no-scrollbar::-webkit-scrollbar {
        display: none;
    }
    .truncate-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
`;
document.head.appendChild(style);

export default MobileView;
